/**
 * Shared category management — ONE list used by Courses and the Question bank (GET/POST/PUT/DELETE /categories).
 *
 *   const cats = useCategoryManagerPane({ api, onChange });
 *   cats.categories / cats.reload()                      the list (with course_count + question_count)
 *   cats.open() / cats.close() / cats.side               the "Manage categories" side pane
 *
 * Categories nest to any depth (parent_id). The pane shows the tree (indented), each row has
 * "Add sub-category", edit and delete; the add / edit forms carry a "Parent category" select.
 * Deleting a category moves its sub-categories up one level.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { paneForm, PaneFooter } from '../shell/SidePane';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { FolderOpen, Pencil, Trash, Plus, TreeStructure } from '@phosphor-icons/react';

export const CATEGORY_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#64748b', '#f97316'];
const EMPTY = { name: '', description: '', color: '#6366f1', parent_id: '' };

/** Options for a parent / category <select>: indented by depth, in tree order. */
const indent = (c) => `${'\u00a0\u00a0\u00a0'.repeat(c.depth || 0)}${c.depth ? '└ ' : ''}${c.name}`;

export function useCategories(api) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/categories'); setCategories(r.data || []); } catch { setCategories([]); } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { reload(); }, [reload]);
  return { categories, reload, loading };
}

function ColorPicker({ value, onChange, size = 'w-6 h-6', count }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {(count ? CATEGORY_COLORS.slice(0, count) : CATEGORY_COLORS).map((c) => (
        <button key={c} type="button" className={`${size} rounded transition-transform ${value === c ? 'ring-2 ring-offset-1 ring-shell-primary scale-110' : ''}`} style={{ backgroundColor: c }} onClick={() => onChange(c)} title={c} aria-label={`Colour ${c}`} />
      ))}
    </div>
  );
}

export function useCategoryManagerPane({ api, onChange }) {
  const { categories, reload } = useCategories(api);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  const open = () => { setIsOpen(true); setEditing(null); setForm({ ...EMPTY }); reload(); };
  const close = () => { setIsOpen(false); setEditing(null); setForm({ ...EMPTY }); };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('Category name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      if (editing) { await api.put(`/categories/${editing.id}`, payload); toast.success('Category updated'); setEditing(null); }
      else { await api.post('/categories', payload); toast.success(payload.parent_id ? 'Sub-category created' : 'Category created'); }
      setForm({ ...EMPTY, parent_id: form.parent_id });
      await reload();
      onChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally { setSaving(false); }
  };

  const remove = async (cat) => {
    const inUse = (cat.course_count || 0) + (cat.question_count || 0);
    const subs = cat.child_count ? ` Its ${cat.child_count} sub-categor${cat.child_count === 1 ? 'y moves' : 'ies move'} up one level.` : '';
    if (!window.confirm(`Delete category "${cat.name}"?${inUse ? ` ${cat.course_count || 0} course(s) and ${cat.question_count || 0} question(s) will become uncategorised.` : ''}${subs}`)) return;
    try { await api.delete(`/categories/${cat.id}`); toast.success('Category deleted'); await reload(); onChange?.(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete category'); }
  };

  let side = null;
  if (isOpen && editing) {
    const f = paneForm({ id: 'edit-category-form', onSubmit: save, onCancel: () => setEditing(null), submitLabel: 'Update', submitting: saving, children: (
      <>
        <div className="field"><Label>Category name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="category-name-input" /></div>
        <div className="field"><Label>Parent category</Label>
          <select className="form-select" value={form.parent_id || ''} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} data-testid="category-parent-select">
            <option value="">— Top level —</option>
            {categories.filter((c) => c.id !== editing.id && !(c.path || '').startsWith(`${editing.path} / `)).map((c) => <option key={c.id} value={c.id}>{indent(c)}</option>)}
          </select>
        </div>
        <div className="field"><Label>Description</Label><Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
        <div className="field"><Label>Colour</Label><ColorPicker value={form.color} onChange={(c) => setForm({ ...form, color: c })} size="w-7 h-7" /></div>
      </>
    ) });
    side = { open: true, onOpenChange: close, title: 'Edit category', subtitle: editing.name, icon: Pencil, children: f.body, footer: <PaneFooter form="edit-category-form" onCancel={() => setEditing(null)} cancelLabel="Back" submitLabel="Update" submitting={saving} />, testid: 'edit-category-pane', storageKey: 'pane-manage-categories' };
  } else if (isOpen) {
    side = {
      open: true, onOpenChange: close, title: 'Manage categories', subtitle: 'Shared by Courses and the Question bank', icon: FolderOpen, testid: 'manage-categories-pane', storageKey: 'pane-manage-categories',
      children: (
        <div className="space-y-4">
          <div className="rounded border border-border divide-y" data-testid="category-list">
            {categories.length === 0 ? (
              <p className="text-center text-muted-foreground text-[13px] py-5">No categories yet — add the first one below.</p>
            ) : categories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-3 py-2 pr-2" style={{ paddingLeft: `${12 + (cat.depth || 0) * 18}px` }} data-testid={`category-row-${cat.id}`}>
                {cat.depth ? <span className="text-muted-foreground/60 -ml-1 text-[12px]">└</span> : null}
                <span className="w-3.5 h-3.5 rounded flex-shrink-0" style={{ backgroundColor: cat.color }} />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium truncate">{cat.name}</p>
                  <p className="field-hint">{(cat.total_course_count ?? cat.course_count) || 0} course{(cat.total_course_count ?? cat.course_count) === 1 ? '' : 's'} · {(cat.total_question_count ?? cat.question_count) || 0} question{(cat.total_question_count ?? cat.question_count) === 1 ? '' : 's'}{cat.child_count ? ` · ${cat.child_count} sub-categor${cat.child_count === 1 ? 'y' : 'ies'}` : ''}{cat.description ? ` · ${cat.description}` : ''}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(null); setForm({ ...EMPTY, parent_id: cat.id }); setTimeout(() => document.querySelector('[data-testid="new-category-input"]')?.focus(), 0); }} title={`Add a sub-category under ${cat.name}`} data-testid={`category-add-sub-${cat.id}`}><TreeStructure className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(cat); setForm({ name: cat.name, description: cat.description || '', color: cat.color || '#6366f1', parent_id: cat.parent_id || '' }); }} title="Edit"><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(cat)} title="Delete"><Trash className="w-4 h-4 text-destructive" /></Button>
              </div>
            ))}
          </div>
          <form id="create-category-form" onSubmit={save} className="space-y-2 border-t border-border pt-3">
            <Label>{form.parent_id ? 'Add a sub-category' : 'Add a category'}</Label>
            <select className="form-select" value={form.parent_id || ''} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} data-testid="new-category-parent">
              <option value="">— Top level —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{indent(c)}</option>)}
            </select>
            <div className="flex gap-2">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.parent_id ? "e.g. Algebra" : "e.g. Mathematics"} className="flex-1" data-testid="new-category-input" />
              <Button type="submit" size="sm" className="h-8" disabled={saving || !form.name.trim()} data-testid="new-category-submit"><Plus className="w-4 h-4" /> Add</Button>
            </div>
            <ColorPicker value={form.color} onChange={(c) => setForm({ ...form, color: c })} count={10} />
          </form>
        </div>
      ),
      footer: <PaneFooter onCancel={close} cancelLabel="Close" />,
    };
  }

  return { categories, reload, open, close, isOpen, side };
}

/** Standard category <select> (shared list) with an optional "Manage…" link. */
export function CategorySelect({ value, onChange, categories, onManage, placeholder = 'No category', testid = 'category-select', className = '' }) {
  return (
    <div className={`flex gap-2 ${className}`}>
      <select className="form-select" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">{placeholder}</option>
        {categories.map((c) => <option key={c.id} value={c.id} title={c.path}>{indent(c)}</option>)}
      </select>
      {onManage && <Button type="button" variant="outline" size="sm" className="h-8 flex-shrink-0" onClick={onManage} title="Manage categories"><FolderOpen className="w-4 h-4" /></Button>}
    </div>
  );
}
